import React, { useEffect, useContext, useState, useMemo } from 'react'
import { useForm, FormProvider } from 'react-hook-form'
import { Grid } from '@mui/material'
import { Add, Edit } from '@mui/icons-material'
import { useParams } from 'react-router-dom'
import { useSelector } from 'react-redux'
import store from 'state/store'
import ContentLayoutContext from 'components/layouts/ContentLayoutContextProvider'
import { transformStrToCapital } from 'utils/router'
import { showFormModal } from 'state/ui'
import FORM_MODALS from 'utils/ui/modals'
import RouterPrompt from 'components/generic/modals/router-prompt/RouterPrompt'
import { useGetMaterialTypesQuery } from 'services/materialTypes'
import { transformMaterialTypes } from 'components/containers/commodities/commoditiesHelpers'
import PriceEntriesList from 'components/containers/commodities/price-entries/PriceEntriesList'
import { MODAL_VIEW_MODES } from 'utils/constants'

const CommoditiesMaterialContainer = () => {
  const context = useContext(ContentLayoutContext)
  const { commodityId } = useParams()

  const [filter, setFilter] = useState({ search: '', commodityId })
  const [dirtyFields, setDirtyFields] = useState({})
  const [paginationOptions, setPaginationOptions] = useState({ page: 1, pageSize: 50 })
  const { data, isFetching } = useGetMaterialTypesQuery(
    { filter: JSON.stringify(filter), ...paginationOptions },
    { skip: !commodityId },
  )

  const methods = useForm()
  const { defaultCurrency, defaultYardUm } = useSelector(state => state.settings)

  useEffect(() => {
    context.setContainerTitle(`${transformStrToCapital(data?.Items?.[0]?.commodityName)} Prices`)

    const pageActions = [
      {
        icon: <Edit />,
        text: 'Edit Commodity',
        id: 'edit-commodity',
        onClick: () => {
          store.dispatch(
            showFormModal({
              formModalName: FORM_MODALS.NEW_COMMODITY,
              formModalProps: { viewMode: MODAL_VIEW_MODES.EDIT, commodityId },
            }),
          )
        },
      },
      {
        icon: <Add />,
        text: 'Add Material',
        id: 'add-material-type',
        onClick: () => {
          store.dispatch(showFormModal({ formModalName: FORM_MODALS.NEW_MATERIAL }))
        },
      },
    ]

    context.setContainerActions(pageActions)
  }, [data])

  const transformedPriceEntries = useMemo(
    () => transformMaterialTypes(data?.Items, defaultCurrency, defaultYardUm),
    [data, defaultCurrency, defaultYardUm],
  )

  return (
    <form>
      <RouterPrompt
        title="Leave This Page"
        question="Are you sure you want to leave this page?"
        description="Changes to the ticket will be cancelled and all unsaved data on the ticket will be lost."
        when={Object.keys(dirtyFields).length > 0}
      />
      <FormProvider {...methods}>
        <Grid p={3} sx={{ height: '100%' }} container flexDirection="column">
          <PriceEntriesList
            priceEntriesList={transformedPriceEntries}
            loading={isFetching}
            filter={filter}
            setFilter={setFilter}
            setDirtyFields={setDirtyFields}
            rowCount={data?.totalResultsCount}
            paginationOptions={paginationOptions}
            setPaginationOptions={setPaginationOptions}
          />
        </Grid>
      </FormProvider>
    </form>
  )
}

export default CommoditiesMaterialContainer
